import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Link,
  Table,
  Text,
  TextField,
  Tooltip
} from '@radix-ui/themes'
import { Ops } from '@willie/schemas'
import { useAuth } from '~/auth/AuthProvider'
import { useTRPC } from '~/utils/trpc'
import { todayIso } from '~/utils/date'
import { loggerMakerLabel } from '~/features/installations/constants'
import { signImageRefsForProviderOrder } from '~/features/installations/service'
import { findOrderIssue, isOrderBlocked, type OrderIssue } from '~/features/installations/orderValidation'
import {
  ORDERABLE_MAKERS,
  ORDER_EXPORT_SPEC,
  buildCsv,
  downloadCsv,
  findMissing,
  isOrderable,
  type Installation,
  type OrderableProvider
} from '~/features/installations/exports'

const SEARCH_DEBOUNCE_MS = 300

interface ReviewRow {
  installation: Installation
  missingColumns: string[]
  issue: OrderIssue | null
}

interface ProviderResult {
  provider: OrderableProvider
  rows: ReviewRow[]
  csv: string
  filename: string
}

export const PlaceOrder = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { supabaseClient } = useAuth()

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedById, setSelectedById] = useState<Map<number, Installation>>(new Map())
  const [results, setResults] = useState<ProviderResult[] | null>(null)
  const [confirmedProviders, setConfirmedProviders] = useState<Set<OrderableProvider>>(new Set())
  const [preparing, setPreparing] = useState(false)
  const [prepareError, setPrepareError] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const installationsQuery = useQuery(
    trpc.ops.stationInstallations.list.queryOptions({ search: debouncedSearch || undefined })
  )
  const orgsQuery = useQuery(trpc.ops.orgs.list.queryOptions())

  const orgNameById = useMemo(() => new Map((orgsQuery.data ?? []).map((o) => [o.id, o.name])), [orgsQuery.data])
  const resolveOrgName = (installation: Installation): string => orgNameById.get(installation.org_id) ?? '—'

  // Only not-yet-ordered installations are listed — an installation can't be ordered twice.
  const unordered = useMemo(
    () =>
      (installationsQuery.data ?? [])
        .filter((i) => isOrderable(i.logger_maker))
        .filter((i) => !i.order_placed_date),
    [installationsQuery.data]
  )
  const blocked = useMemo(() => unordered.filter(isOrderBlocked), [unordered])
  const listed = useMemo(() => [...unordered.filter((i) => !isOrderBlocked(i)), ...blocked], [unordered, blocked])

  const warned = useMemo(() => unordered.filter((i) => findOrderIssue(i)?.severity === 'warning'), [unordered])
  const shaypWarnedCount = warned.filter((i) => i.logger_maker === 'shayp').length
  const compatibilityBlockedCount = blocked.filter(
    (i) => !Ops.StationInstallations.isOrderableCompatibility(i.compatibility_status)
  ).length
  const efentoUnmappedCount = blocked.filter((i) => findOrderIssue(i)?.reason === 'efento_unmapped_profile').length

  // Keyed by id so selections persist across searches/filters — a row picked
  // under one search stays selected after the list refetches for another.
  const selectedInstallations = useMemo(() => [...selectedById.values()], [selectedById])

  const toggle = (installation: Installation) => {
    if (isOrderBlocked(installation)) return
    setSelectedById((prev) => {
      const next = new Map(prev)
      if (next.has(installation.installation_id)) next.delete(installation.installation_id)
      else next.set(installation.installation_id, installation)
      return next
    })
  }

  const confirmOrderMutation = useMutation(
    trpc.ops.stationInstallations.confirmOrder.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
      }
    })
  )

  const handlePrepare = async () => {
    setPreparing(true)
    setPrepareError(null)
    try {
      const groups = ORDERABLE_MAKERS.map((provider) => ({
        provider,
        spec: ORDER_EXPORT_SPEC[provider],
        installations: selectedInstallations.filter((i) => i.logger_maker === provider)
      })).filter((group) => group.installations.length > 0)

      // Signed before the CSV is built: buildCsv is synchronous, and confirmOrder is
      // handed the finished text, so the server cannot retrofit a link afterwards
      // without re-parsing provider-facing CSV.
      //
      // Only the rows of specs that emit an image are signed. Signing everything would
      // let a storage outage block a provider whose CSV carries no image column.
      const refsToSign = groups
        .filter((group) => group.spec.needsSignedImages)
        .flatMap((group) => group.installations.map((i) => i.pre_installation_image_url))
      const signedImageUrls =
        refsToSign.length > 0 ? await signImageRefsForProviderOrder(supabaseClient, refsToSign) : {}

      const today = todayIso()
      const grouped = groups.map(({ provider, spec, installations }): ProviderResult => {
        // A blocked row can only get here if its profile changed after it was selected. It stays
        // in `rows` so the reviewer sees why, but never reaches the provider-facing CSV.
        const allowed = installations.filter((installation) => !isOrderBlocked(installation))
        const missingById = new Map(
          findMissing(spec, allowed, resolveOrgName, signedImageUrls).map((m) => [
            m.installationId,
            m.missingColumns
          ])
        )
        return {
          provider,
          rows: installations.map((installation) => ({
            installation,
            missingColumns: missingById.get(installation.installation_id) ?? [],
            issue: findOrderIssue(installation)
          })),
          csv: buildCsv(spec, allowed, resolveOrgName, signedImageUrls),
          filename: `${spec.filenamePrefix}-${today}.csv`
        }
      })
      setResults(grouped)
    } catch (err) {
      setPrepareError(err instanceof Error ? err.message : 'Could not prepare the order')
    } finally {
      setPreparing(false)
    }
  }

  const handleConfirm = async (result: ProviderResult) => {
    await confirmOrderMutation.mutateAsync({
      provider: result.provider,
      filename: result.filename,
      csv: result.csv,
      installationIds: result.rows
        .filter((r) => !isOrderBlocked(r.installation))
        .map((r) => r.installation.installation_id)
    })
    setConfirmedProviders((prev) => new Set(prev).add(result.provider))
  }

  if (results) {
    return (
      <Flex direction="column" gap="4">
        <Flex align="center" justify="between" gap="3">
          <Heading size="6">Review order</Heading>
          <Flex gap="3">
            <Button
              variant="soft"
              color="gray"
              disabled={confirmedProviders.size > 0}
              onClick={() => {
                setResults(null)
                setConfirmedProviders(new Set())
              }}
            >
              Back
            </Button>
            <Button asChild variant="soft" color="gray">
              <RouterLink to="/orders">Done</RouterLink>
            </Button>
          </Flex>
        </Flex>
        <Text size="2" color="gray">
          Confirming sends the CSV to the provider and marks its installations as ordered. Downloading the CSV does
          not mark anything.
        </Text>

        {results.map((result) => {
          const confirmed = confirmedProviders.has(result.provider)
          const confirming =
            confirmOrderMutation.isPending && confirmOrderMutation.variables?.provider === result.provider
          const blocked = result.rows.some((r) => isOrderBlocked(r.installation))
          return (
            <Box
              key={result.provider}
              className="rounded-[var(--radius-3)] border border-[color:var(--gray-6)] p-3"
            >
              <Flex align="center" justify="between" gap="3" mb="2">
                <Text size="2" weight="medium">
                  {loggerMakerLabel[result.provider]} — {result.rows.length} installation
                  {result.rows.length === 1 ? '' : 's'}
                </Text>
                <Flex gap="3" align="center">
                  <Link
                    href="#"
                    size="2"
                    onClick={(e) => {
                      e.preventDefault()
                      downloadCsv(result.filename, result.csv)
                    }}
                  >
                    Download CSV
                  </Link>
                  <Button
                    onClick={() => handleConfirm(result)}
                    loading={confirming}
                    disabled={confirming || confirmed || blocked}
                    color={confirmed ? 'green' : undefined}
                  >
                    {confirmed ? 'Ordered ✓' : 'Confirm order'}
                  </Button>
                </Flex>
              </Flex>
              {blocked && (
                <Text as="p" size="1" color="red" mb="2">
                  Remove the blocked installations from this selection before confirming.
                </Text>
              )}
              <Flex direction="column" gap="1">
                {result.rows.map(({ installation, missingColumns, issue }) => {
                  const complete = missingColumns.length === 0
                  const block = issue?.severity === 'block' ? issue : null
                  const warning = issue?.severity === 'warning' ? issue : null
                  return (
                    <Flex
                      key={installation.installation_id}
                      align="center"
                      justify="between"
                      gap="3"
                      className={cn('rounded px-2 py-1', {
                        'bg-[var(--red-a3)]': block,
                        'bg-[var(--green-a3)]': !block && complete,
                        'bg-[var(--amber-a3)]': !block && !complete
                      })}
                    >
                      <Flex align="center" gap="2" className="min-w-0">
                        <Text size="1" className="font-mono shrink-0">
                          #{installation.installation_id}
                        </Text>
                        <Text size="1" className="truncate">
                          {installation.name ?? '—'}
                        </Text>
                        <Text size="1" color="gray" className="truncate">
                          {resolveOrgName(installation)} · {installation.site?.name ?? '—'}
                        </Text>
                      </Flex>
                      <Flex align="center" gap="2" className="shrink-0">
                        {warning && (
                          <Tooltip content={warning.detail}>
                            <Text size="1" color="amber">
                              {warning.label}
                            </Text>
                          </Tooltip>
                        )}
                        {block ? (
                          <Tooltip content={block.detail}>
                            <Text size="1" color="red">
                              {block.label}
                            </Text>
                          </Tooltip>
                        ) : complete ? (
                          <Text size="1" color="green">
                            Ready
                          </Text>
                        ) : (
                          <Text size="1" color="amber">
                            Missing: {missingColumns.join(', ')}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  )
                })}
              </Flex>
            </Box>
          )
        })}

        {confirmOrderMutation.error && (
          <Text color="red" size="2">
            {confirmOrderMutation.error.message}
          </Text>
        )}
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" justify="between" gap="3">
        <Heading size="6">Place order</Heading>
        <Flex gap="3">
          <Button asChild variant="soft" color="gray">
            <RouterLink to="/orders">Cancel</RouterLink>
          </Button>
          <Button onClick={handlePrepare} loading={preparing} disabled={selectedById.size === 0 || preparing}>
            Prepare Order ({selectedById.size})
          </Button>
        </Flex>
      </Flex>
      <Text size="2" color="gray">
        Search and select Shayp or Efento installations. Prepare Order builds one CSV per provider to review before
        confirming.
      </Text>

      <TextField.Root
        placeholder="Name, installation ID, internal ID, contact…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
      />

      {shaypWarnedCount > 0 && (
        <Text size="1" color="amber">
          {shaypWarnedCount} installation{shaypWarnedCount === 1 ? ' is' : 's are'} set to Shayp but should be
          Efento.
        </Text>
      )}
      {compatibilityBlockedCount > 0 && (
        <Text size="1" color="red">
          {compatibilityBlockedCount} installation{compatibilityBlockedCount === 1 ? '' : 's'} can't be ordered
          until {compatibilityBlockedCount === 1 ? 'its' : 'their'} compatibility is checked or approved.
        </Text>
      )}
      {efentoUnmappedCount > 0 && (
        <Text size="1" color="red">
          {efentoUnmappedCount} Efento installation{efentoUnmappedCount === 1 ? '' : 's'} can't be ordered until{' '}
          {efentoUnmappedCount === 1 ? 'its profile has' : 'their profiles have'} a parts list.
        </Text>
      )}

      {installationsQuery.isLoading ? (
        <Text color="gray">Loading…</Text>
      ) : installationsQuery.error ? (
        <Text color="red" size="2">
          {installationsQuery.error.message}
        </Text>
      ) : listed.length === 0 ? (
        <Box py="5">
          <Text color="gray" size="2">
            No Shayp or Efento installations match.
          </Text>
        </Box>
      ) : (
        <Table.Root size="1" variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell />
              <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Org</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Site</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Profile</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Provider</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {listed.map((row) => {
              const issue = findOrderIssue(row)
              const blocked = issue?.severity === 'block'
              return (
                <Table.Row
                  key={row.installation_id}
                  className={cn(
                    blocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[var(--gray-a2)]'
                  )}
                  onClick={() => toggle(row)}
                >
                  <Table.Cell>
                    <Checkbox checked={selectedById.has(row.installation_id)} disabled={blocked} />
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" className="font-mono">
                      #{row.installation_id}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2">{resolveOrgName(row)}</Text>
                  </Table.Cell>
                  <Table.Cell>{row.name ?? '—'}</Table.Cell>
                  <Table.Cell>
                    <Text size="2">{row.site?.name ?? '—'}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2">{row.installation_profile ?? '—'}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex align="center" gap="2">
                      <Badge variant="soft" size="1">
                        {loggerMakerLabel[row.logger_maker as OrderableProvider]}
                      </Badge>
                      {issue && (
                        <Tooltip content={issue.detail}>
                          <Badge variant="soft" size="1" color={blocked ? 'red' : 'amber'}>
                            {issue.label}
                          </Badge>
                        </Tooltip>
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
      )}

      {prepareError && (
        <Text color="red" size="2">
          {prepareError}
        </Text>
      )}
    </Flex>
  )
}
